import {useCallback, useEffect} from 'react';
import {useLazyQuery} from '@apollo/client';
import {useAppDispatch} from '@/Pages/Common/hooks/useAppDispatch';
import {useAppSelector} from '@/Pages/Common/hooks/useAppSelector';
import getVehicleTypesQuery from '../graphql/getVehicleTypesQuery';
import {receivedVehicleTypes} from '../store/slice';
import {VehicleType} from '../store/types';

interface UseVehicleTypesResult {
  vehicleTypes: VehicleType[] | null;
  refetchVehicleTypes: () => Promise<void>;
}

export const useVehicleTypes = (): UseVehicleTypesResult => {
  const vehicleTypes = useAppSelector(({vehicleTypes}) => vehicleTypes.list);
  const dispatch = useAppDispatch();

  const [fetchVehicleTypes] = useLazyQuery<{busTypes: VehicleType[]}>(
    getVehicleTypesQuery,
    {
      errorPolicy: 'all',
      fetchPolicy: 'no-cache'
    }
  );

  const handleError = useCallback(
    (errorMessage: string) => {
      console.error(errorMessage);
      dispatch(receivedVehicleTypes([]));
    },
    [dispatch]
  );

  const getVehicleTypes = useCallback(
    async (force = false) => {
      // Only fetch if vehicleTypes are missing, unless forced
      if (!force && Array.isArray(vehicleTypes)) return;

      try {
        const {data, error} = await fetchVehicleTypes();

        if (error) {
          const errorMessage =
            error.message ?? 'Failed to fetch vehicle types.';
          handleError(errorMessage);
          return;
        }

        const fetchedVehicleTypes = data?.busTypes ?? [];
        dispatch(receivedVehicleTypes(fetchedVehicleTypes));
      } catch (err) {
        handleError(`Unexpected error while fetching vehicle types: ${err}`);
      }
    },
    [dispatch, fetchVehicleTypes, handleError, vehicleTypes]
  );

  useEffect(() => {
    getVehicleTypes();
  }, []); // eslint-disable-line

  const refetchVehicleTypes = useCallback(
    () => getVehicleTypes(true),
    [getVehicleTypes]
  );

  return {vehicleTypes, refetchVehicleTypes};
};
